/**
 * Mosqify Auth Context
 * Modern React authentication context with best practices
 * - Secure token storage (IndexedDB + localStorage fallback)
 * - Automatic token refresh
 * - Service worker integration
 * - Clean separation of concerns
 */
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import neonAuthService from './services/neonAuthService';
import mosqifyApiService from './services/mosqifyApiService';
import { authStorage } from './utils/authStorage';
import { tokenManager } from './utils/tokenManager';
import { createAuthError, AuthErrorCode, isAuthError, getAuthErrorMessage } from './utils/authTypes';
import config from './config/appConfig';
import { clearDailyMissionProgress } from './services/dailyMissionService';
import { clearPrayerJournal } from './services/prayerJournalService';
import { clearHifdhProgress } from './services/hifdhProgressService';
import { clearMengajiStreak } from './services/mengajiStreakService';

const clearLocalUserData = () => {
  clearDailyMissionProgress();
  clearPrayerJournal();
  clearHifdhProgress();
  clearMengajiStreak();
};
import { identifyUser, resetPosthog, captureEvent } from './posthogClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  // True only while the stored session is being restored. Action handlers (login,
  // register, ...) must not toggle it, or App unmounts the calling screen mid-request.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Setup token refresh callback
   * Centralized refresh logic
   */
  const setupTokenRefresh = useCallback(async () => {
    const refreshCallback = async (currentToken) => {
      try {
        // Always use mobile refresh endpoint which handles JWT token refresh
        // The mobile endpoint bridges Neon Auth if needed
        console.log('[AuthContext] Refreshing token via mobile auth endpoint...');
        const newToken = await mosqifyApiService.refreshToken();
        setToken(newToken);
        setError(null);
        return newToken;
      } catch (error) {
        console.error('[AuthContext] Token refresh failed:', error);
        // Clear auth on refresh failure
        setUser(null);
        setToken(null);
        setError(createAuthError(AuthErrorCode.REFRESH_FAILED, getAuthErrorMessage(error)));
        await authStorage.clear();
        throw error;
      }
    };

    await tokenManager.start(refreshCallback);
  }, []);

  /**
   * Initialize authentication on app start
   * Restores session from storage and validates token
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Initialize auth storage
        await authStorage.init();

        // Get stored token and user
        const storedToken = await authStorage.getToken();
        const storedUser = await authStorage.getUser();

        if (storedToken) {
          // Set token in service (for API fallback)
          await mosqifyApiService.setToken(storedToken);
          
          // Try to get fresh user data to validate token
          try {
            let user;
            // Always use API to get current user (validates JWT token)
            // The stored token should be a JWT from mobile login endpoint
            const userData = await mosqifyApiService.getCurrentUser();
            user = userData.data || userData;
            
            setUser(user);
            setToken(storedToken);
            await authStorage.setUser(user);
            identifyUser(user.id, { email: user.email, role: user.role });

            // Start automatic token refresh
            await setupTokenRefresh();
          } catch (error) {
            // Only trust the cached user when we are offline. A definitive auth
            // failure (401, failed refresh) must drop the session, otherwise the UI
            // stays "authenticated" while storage and the API service hold no token.
            // Offline, timeouts and 5xx (incl. the service worker's synthetic 503) are transient.
            const httpStatus = error?.response?.status;
            const isTransient = Boolean(error?.isNetworkError) || (typeof httpStatus === 'number' && httpStatus >= 500);
            if (storedUser && isTransient) {
              setUser(storedUser);
              setToken(storedToken);
              identifyUser(storedUser.id, { email: storedUser.email, role: storedUser.role });
              // Still try to setup refresh, it will fail gracefully
              try {
                await setupTokenRefresh();
              } catch (refreshError) {
                console.warn('[AuthContext] Token refresh setup failed, user may need to re-login');
              }
            } else {
              // Clear invalid token and any in-memory session state
              setUser(null);
              setToken(null);
              await authStorage.clear();
              await mosqifyApiService.clearToken();
            }
          }
        } else if (storedUser) {
          // User data exists but no token - clear it
          await authStorage.removeUser();
        }
      } catch (error) {
        console.error('[AuthContext] Auth initialization failed:', error);
        setError(createAuthError(AuthErrorCode.UNKNOWN, 'Gagal memuat sesi'));
        // Clear invalid auth data
        await authStorage.clear();
        await mosqifyApiService.clearToken();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      tokenManager.stop();
    };
  }, [setupTokenRefresh]);

  /**
   * Handle successful authentication
   * Centralized auth success logic
   */
  const handleAuthSuccess = useCallback(async (authData) => {
    // With Neon Auth + email verification, token might not be provided until verified
    // Check if we have at least a user
    if (!authData?.user && !authData?.access_token) {
      throw createAuthError(AuthErrorCode.LOGIN_FAILED, 'No authentication data received');
    }

    // Ensure user data exists
    if (!authData.user) {
      console.warn('[AuthContext] No user data in auth response, but access token exists');
      // If we have token but no user, try to get user from session
      if (config.neonAuth.enabled && authData.access_token) {
        try {
          const sessionData = await neonAuthService.getSession();
          if (sessionData.user) {
            authData.user = sessionData.user;
          }
        } catch (error) {
          console.error('[AuthContext] Failed to get user from session:', error);
        }
      }
    }

    // Set user data
    if (authData.user) {
      setUser(authData.user);
      await authStorage.setUser(authData.user);
      // Identify user in PostHog for analytics (id, email, role)
      identifyUser(authData.user.id, {
        email: authData.user.email,
        role: authData.user.role,
      });
      captureEvent('login_success', {
        userId: authData.user.id,
        method: authData.access_token ? 'credentials' : 'session',
      });
    }

    setError(null);

    // Only set token if provided
    if (authData.access_token) {
      setToken(authData.access_token);
      await authStorage.setToken(authData.access_token, authData.expires_in);
      // Start automatic token refresh
      await setupTokenRefresh();
    }

    return authData;
  }, [setupTokenRefresh]);

  /**
   * Normalize API response format
   * Handles different response formats from API
   */
  const normalizeAuthResponse = useCallback((response) => {
    if (response.access_token) {
      return response;
    }
    if (response.data?.access_token) {
      return response.data;
    }
    // Handle responses that require email verification (no access token yet)
    if (response.requiresVerification) {
      return response;
    }
    if (response.data?.requiresVerification) {
      return response.data;
    }
    throw createAuthError(AuthErrorCode.LOGIN_FAILED, 'Invalid response format from API');
  }, []);

  /**
   * Login function
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Auth data
   */
  const login = useCallback(async (email, password) => {
    try {
      setError(null);

      // Always use mobile login endpoint which seamlessly bridges Neon Auth to JWT
      // The mobile endpoint handles Neon Auth internally and returns JWT tokens
      console.log('[AuthContext] Logging in via mobile auth endpoint (Neon Auth bridge)...');
      const response = await mosqifyApiService.login(email, password);
      const authData = normalizeAuthResponse(response);
      
      const result = await handleAuthSuccess(authData);
      console.log('[AuthContext] Login successful:', { 
        hasUser: !!result.user, 
        hasToken: !!result.access_token,
        userEmail: result.user?.email,
        tokenType: result.access_token?.startsWith('eyJ') ? 'JWT' : 'Session'
      });
      return result;
    } catch (error) {
      // Check if error is about email verification
      if (error.code === 'EMAIL_NOT_VERIFIED' || (error.message && error.message.includes('belum diverifikasi'))) {
        // Preserve the verification error with user info
        const verificationError = createAuthError(
          AuthErrorCode.LOGIN_FAILED, 
          error.message || 'Email Anda belum diverifikasi. Silakan cek inbox dan klik link verifikasi.'
        );
        verificationError.code = 'EMAIL_NOT_VERIFIED';
        verificationError.user = error.user;
        verificationError.email = error.email || email;
        setError(verificationError);
        throw verificationError;
      }
      
      const errorMessage = getAuthErrorMessage(error);
      const authError = isAuthError(error)
        ? error
        : createAuthError(AuthErrorCode.LOGIN_FAILED, errorMessage);
      
      setError(authError);
      throw authError;
    }
  }, [normalizeAuthResponse, handleAuthSuccess]);

  /**
   * Register function
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Auth data or verification required
   */
  const register = useCallback(async (userData) => {
    try {
      setError(null);

      // Always use mobile register endpoint which seamlessly bridges Neon Auth to JWT
      // The mobile endpoint handles Neon Auth internally and returns JWT tokens
      console.log('[AuthContext] Registering via mobile auth endpoint (Neon Auth bridge)...');
      const response = await mosqifyApiService.register(userData);
      const authData = normalizeAuthResponse(response);
      
      // Handle email verification requirement from mobile endpoint
      if (authData.requiresVerification) {
        // Return response indicating verification is required
        return {
          access_token: null,
          user: authData.user,
          expires_in: 0,
          requiresVerification: true,
          email: authData.email || userData.email,
        };
      }
      
      return await handleAuthSuccess(authData);
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      const authError = isAuthError(error)
        ? error
        : createAuthError(AuthErrorCode.LOGIN_FAILED, errorMessage || 'Registrasi gagal. Silakan coba lagi.');
      
      setError(authError);
      throw authError;
    }
  }, [normalizeAuthResponse, handleAuthSuccess]);

  /**
   * Verify email with token
   * @param {string} token - Verification token from email
   * @returns {Promise<Object>} Auth data
   */
  const verifyEmail = useCallback(async (token) => {
    try {
      setError(null);

      if (!config.neonAuth.enabled) {
        throw new Error('Email verification hanya tersedia dengan Neon Auth');
      }

      // The Neon session token is not an API JWT, so do not store it; the user logs in afterwards.
      return await neonAuthService.verifyEmail(token);
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      const authError = isAuthError(error)
        ? error
        : createAuthError(AuthErrorCode.LOGIN_FAILED, errorMessage || 'Verifikasi email gagal. Silakan coba lagi.');
      
      setError(authError);
      throw authError;
    }
  }, []);

  /**
   * Resend verification email
   * @param {string} email - User email
   * @returns {Promise<Object>} Response
   */
  const resendVerificationEmail = useCallback(async (email) => {
    try {
      setError(null);

      if (!config.neonAuth.enabled) {
        throw new Error('Email verification hanya tersedia dengan Neon Auth');
      }

      return await neonAuthService.resendVerificationEmail(email);
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      const authError = isAuthError(error)
        ? error
        : createAuthError(AuthErrorCode.UNKNOWN, errorMessage || 'Gagal mengirim ulang email verifikasi.');
      
      setError(authError);
      throw authError;
    }
  }, []);

  /**
   * Refresh token function
   * @returns {Promise<string>} New token
   */
  const refreshToken = useCallback(async () => {
    try {
      setError(null);
      let newToken;
      // Use Neon Auth if enabled, otherwise fallback to API
      if (config.neonAuth.enabled) {
        const response = await neonAuthService.refreshSession();
        newToken = response.access_token;
      } else {
        newToken = await mosqifyApiService.refreshToken();
      }
      setToken(newToken);
      return newToken;
    } catch (error) {
      // Clear auth data on refresh failure
      setToken(null);
      setUser(null);
      setError(createAuthError(AuthErrorCode.REFRESH_FAILED, getAuthErrorMessage(error)));
      await authStorage.clear();
      throw error;
    }
  }, []);

  /**
   * Logout function
   * Clears all auth state and stops token refresh
   */
  const logout = useCallback(async () => {
    try {
      // Stop token refresh
      tokenManager.stop();
      
      // Use Neon Auth if enabled, otherwise fallback to API
      if (config.neonAuth.enabled) {
        await neonAuthService.signOut();
      } else {
        await mosqifyApiService.logout();
      }
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setError(null);
      await mosqifyApiService.clearToken();
      await authStorage.clear();
      // Shared phones: the next person must not inherit this user's cart or mosque.
      try {
        localStorage.removeItem('cartItems');
        localStorage.removeItem('selected_mosque');
      } catch {
        /* storage unavailable */
      }
      clearLocalUserData();
      resetPosthog();
    }
  }, []);

  // The API service dispatches this when a 401 could not be recovered by a token refresh
  useEffect(() => {
    const onExpired = async () => {
      tokenManager.stop();
      setUser(null);
      setToken(null);
      await authStorage.clear();
      clearLocalUserData();
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  /**
   * Delete account (soft-delete)
   * Deactivates account and clears all auth state
   * @param {Object} params - Deletion params
   * @param {string} [params.password] - Password for confirmation (regular accounts)
   * @param {string} [params.confirmation] - Text confirmation e.g. "HAPUS" (Google OAuth accounts)
   * @returns {Promise<Object>} Response
   */
  const deleteAccount = useCallback(async ({ password, confirmation } = {}) => {
    try {
      setError(null);

      const response = await mosqifyApiService.deleteAccount({ password, confirmation });

      captureEvent('account_deleted', { userId: user?.id });

      // Clean up auth state (same as logout)
      tokenManager.stop();
      setUser(null);
      setToken(null);
      setError(null);
      await mosqifyApiService.clearToken();
      await authStorage.clear();
      resetPosthog();

      return response;
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      const authError = isAuthError(error)
        ? error
        : createAuthError(AuthErrorCode.UNKNOWN, errorMessage || 'Gagal menghapus akun.');
      setError(authError);
      throw authError;
    }
  }, [user]);

  /**
   * Refresh user data from API (e.g. after claiming points).
   * @returns {Promise<Object>} Updated user data
   */
  const refreshUser = useCallback(async () => {
    try {
      const userData = await mosqifyApiService.getCurrentUser();
      const updatedUser = userData.data || userData;
      if (updatedUser) {
        setUser(updatedUser);
        await authStorage.setUser(updatedUser);
      }
      return updatedUser;
    } catch (error) {
      console.warn('[AuthContext] refreshUser failed:', error);
      throw error;
    }
  }, []);

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} Updated user data
   */
  const updateProfile = useCallback(async (profileData) => {
    try {
      setError(null);
      const response = await mosqifyApiService.put('/users/profile/me', profileData);
      const updatedUser = response.data || response;
      setUser(updatedUser);
      await authStorage.setUser(updatedUser);
      return response;
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      setError(createAuthError(AuthErrorCode.UNKNOWN, errorMessage));
      throw error;
    }
  }, []);

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Response data
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      setError(null);
      const response = await mosqifyApiService.post('/auth/change-password', {
        oldPassword: currentPassword,
        newPassword,
      });
      return response;
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      setError(createAuthError(AuthErrorCode.UNKNOWN, errorMessage));
      throw error;
    }
  }, []);

  /**
   * Google OAuth login/register
   * Always uses API service for Google OAuth (not Neon Auth)
   * Neon Auth is only used for email/password authentication
   * @param {string|null} idToken - Google ID token (required)
   * @param {Object|null} authData - Pre-fetched auth data from OAuth callback (deprecated, not used)
   * @returns {Promise<Object>} Auth data
   */
  const loginWithGoogle = useCallback(async (idToken = null, authData = null) => {
    try {
      setError(null);

      console.log('[AuthContext] Starting Google OAuth login (using API service)...');
      
      // Always use API service for Google OAuth, regardless of Neon Auth being enabled
      // Neon Auth is only used for email/password authentication
      if (!idToken) {
        throw new Error('Token Google diperlukan untuk masuk');
      }
      
      const response = await mosqifyApiService.googleAuth(idToken);
      console.log('[AuthContext] Google OAuth response received:', { 
        hasAccessToken: !!response.access_token,
        hasUser: !!response.user,
        userEmail: response.user?.email 
      });
      
      const normalizedAuthData = normalizeAuthResponse(response);
      console.log('[AuthContext] Normalized auth data:', { 
        hasAccessToken: !!normalizedAuthData.access_token,
        hasUser: !!normalizedAuthData.user,
        userEmail: normalizedAuthData.user?.email 
      });
      
      const result = await handleAuthSuccess(normalizedAuthData);
      console.log('[AuthContext] Google OAuth login successful');
      return result;
    } catch (error) {
      console.error('[AuthContext] Google OAuth login failed:', error);
      const errorMessage = getAuthErrorMessage(error);
      const authError = isAuthError(error)
        ? error
        : createAuthError(AuthErrorCode.LOGIN_FAILED, errorMessage || 'Google login gagal. Silakan coba lagi.');
      
      setError(authError);
      throw authError;
    }
  }, [normalizeAuthResponse, handleAuthSuccess]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // State
    user,
    token,
    isLoading,
    error,
    isAuthenticated: !!user && !!token,
    
    // Actions
    login,
    register,
    logout,
    deleteAccount,
    refreshToken,
    updateProfile,
    changePassword,
    loginWithGoogle,
    verifyEmail,
    resendVerificationEmail,
    refreshUser,
    
    // Utilities
    clearError: () => setError(null),
  }), [user, token, isLoading, error, login, register, logout, deleteAccount, refreshToken, updateProfile, changePassword, loginWithGoogle, verifyEmail, resendVerificationEmail, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

